import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Sparkles, ArrowUp, Loader2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useHermesChat } from '@/hooks/useHermesChat'
import { useHermesActionExecutor } from '@/hooks/useHermesActionExecutor'
import { useJourneyStore } from '@/stores/journeyStore'
import { useSkerpaContextStore } from '@/stores/skerpaContextStore'
import { MessageBubble, ActionCards, MarkdownContent, extractPills } from './SkerpaMessageContent'
import { SkerpaInterviewMode } from './SkerpaInterviewMode'
import { ProactiveWelcome } from './ProactiveWelcome'
import { ViewNudge } from './ViewNudge'
import { MovesStrip } from './MovesStrip'
import { useProactiveGuide } from '@/hooks/useProactiveGuide'

interface Props {
  isOpen: boolean
  isFullscreen?: boolean
  onViewChange?: (view: string) => void
}

export function SkerpaChatTab({ isOpen, isFullscreen, onViewChange }: Props) {
  const {
    messages, isStreaming, streamingText, statusText, error,
    sendMessage, stopStreaming,
  } = useHermesChat()

  const [input, setInput] = useState('')
  const [interviewQuestions, setInterviewQuestions] = useState<string[] | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const selectedGoal = useJourneyStore(s => s.getSelectedGoal())
  const { executedActions, executingAction, actionError, executeAction } = useHermesActionExecutor(onViewChange)
  const { cards: guideCards } = useProactiveGuide()

  const { pills, lastHadActions } = useMemo(() => {
    if (isStreaming) return { pills: [], lastHadActions: false }
    // Only show pills from the last assistant message IF it's newer than the
    // last CONTEXT_SWITCH marker — otherwise they're stale from a previous view.
    let lastSwitchIdx = -1
    let lastAssistantIdx = -1
    for (let i = messages.length - 1; i >= 0; i--) {
      if (lastAssistantIdx === -1 && messages[i].role === 'assistant') lastAssistantIdx = i
      if (lastSwitchIdx === -1 && messages[i].content.startsWith('[CONTEXT_SWITCH:')) lastSwitchIdx = i
      if (lastAssistantIdx !== -1 && lastSwitchIdx !== -1) break
    }
    if (lastAssistantIdx === -1 || lastAssistantIdx < lastSwitchIdx) return { pills: [], lastHadActions: false }
    const last = messages[lastAssistantIdx]
    return {
      pills: extractPills(last.content),
      lastHadActions: !!(last.actions && last.actions.length > 0),
    }
  }, [messages, isStreaming])

  // Fallback pills: when the last assistant reply has zero pills AND zero
  // action cards, surface the top guide cards as pills so the user always has
  // a forward click. Pulls from the same useProactiveGuide engine as MovesStrip.
  const fallbackPills = useMemo(() => {
    if (pills.length > 0 || lastHadActions || isStreaming || messages.length === 0) return []
    return guideCards.slice(0, 3).map(c => ({ label: c.title, prompt: c.prompt }))
  }, [pills.length, lastHadActions, isStreaming, messages.length, guideCards])

  const visiblePills = pills.length > 0 ? pills : fallbackPills

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages, streamingText])
  useEffect(() => { if (isOpen) setTimeout(() => inputRef.current?.focus(), 100) }, [isOpen])

  // Consume any prefilled prompt from the store (set by "Ask Skerpa" buttons
  // elsewhere in the app, e.g. the property pane). Send once and clear.
  const pendingPrompt = useSkerpaContextStore(s => s.pendingPrompt)
  const consumePendingPrompt = useSkerpaContextStore(s => s.consumePendingPrompt)
  useEffect(() => {
    if (!isOpen || !pendingPrompt || isStreaming) return
    sendMessage(pendingPrompt)
    consumePendingPrompt()
  }, [isOpen, pendingPrompt, isStreaming, sendMessage, consumePendingPrompt])

  const handleSend = useCallback((text?: string) => {
    const msg = text || input
    if (!msg.trim() || isStreaming) return
    sendMessage(msg)
    setInput('')
  }, [input, isStreaming, sendMessage])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }, [handleSend])

  return (
    <>
      {messages.length > 0 && <ViewNudge onSend={handleSend} isStreaming={isStreaming} />}

      <div className={cn('flex-1 overflow-y-auto py-3 space-y-2.5', isFullscreen ? 'px-8 mx-auto w-full max-w-3xl' : 'px-3')}>
        {messages.length === 0 && !isStreaming && <ProactiveWelcome onSend={handleSend} />}

        {messages.map((msg) => {
          if (msg.content.startsWith('[CONTEXT_SWITCH:')) {
            const view = msg.content.match(/navigated to (.+?)\./)?.[1] || ''
            return (
              <div key={msg.id} className="flex items-center gap-2 py-1 text-[10px] text-text-muted">
                <div className="flex-1 border-t border-border-subtle" />
                <span>switched to {view}</span>
                <div className="flex-1 border-t border-border-subtle" />
              </div>
            )
          }
          return (
            <div key={msg.id}>
              <MessageBubble message={msg} />
              {msg.actions && msg.actions.length > 0 && (
                <ActionCards actions={msg.actions} executedActions={executedActions} executingAction={executingAction} onExecute={executeAction} onStartInterview={setInterviewQuestions} />
              )}
            </div>
          )
        })}

        {isStreaming && (
          <div className="flex gap-1.5">
            <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(255,192,95,0.15)' }}>
              <Sparkles className="w-3 h-3" style={{ color: '#d4a82a' }} />
            </div>
            <div className="flex-1 min-w-0">
              {streamingText ? (
                <>
                  <MarkdownContent content={streamingText} />
                  {statusText && (
                    <div className="flex items-center gap-1.5 mt-1.5 py-0.5">
                      <Loader2 className="w-2.5 h-2.5 animate-spin" style={{ color: '#d4a82a' }} />
                      <span className="text-[10.5px] text-text-muted animate-pulse">{statusText}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-1.5 py-0.5">
                  <Loader2 className="w-3 h-3 animate-spin" style={{ color: '#d4a82a' }} />
                  <span className="text-[11px] text-text-muted">{statusText || 'Thinking…'}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {visiblePills.length > 0 && !isStreaming && (
          <div className="flex flex-wrap gap-1 pt-0.5 pl-6">
            {visiblePills.map((pill, i) => (
              <button
                key={i}
                onClick={() => handleSend(pill.prompt)}
                title="Send as a follow-up"
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full text-text-muted bg-warm-50 border border-border-subtle hover:bg-warm-100 hover:text-text-secondary transition-colors leading-tight"
              >
                <span>{pill.label}</span>
              </button>
            ))}
          </div>
        )}

        {(error || actionError) && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error || actionError}</div>}
        <div ref={messagesEndRef} />
      </div>

      {interviewQuestions ? (
        <SkerpaInterviewMode
          questions={interviewQuestions}
          onComplete={(combinedAnswer) => { setInterviewQuestions(null); handleSend(combinedAnswer) }}
          onCancel={() => setInterviewQuestions(null)}
        />
      ) : (
        <>
        {messages.length > 0 && <MovesStrip onSend={handleSend} isFullscreen={isFullscreen} suppress={visiblePills.length > 0} />}
        <div className={cn('border-t border-border-subtle bg-white px-3 py-2.5', isFullscreen && 'px-8 mx-auto w-full max-w-3xl')}>
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={selectedGoal ? `Ask about your ${selectedGoal.name} journey…` : 'Ask Skerpa…'}
              rows={1}
              className={cn(
                'flex-1 resize-none text-[13px] text-text bg-warm-50 rounded-lg',
                'border border-border-subtle px-3 py-2 placeholder:text-text-muted',
                'focus:outline-none focus:ring-1 focus:ring-warm-300 focus:border-warm-300',
                'max-h-24 overflow-y-auto',
              )}
              style={{ minHeight: '36px' }}
            />
            <button
              onClick={() => isStreaming ? stopStreaming() : handleSend()}
              disabled={!isStreaming && !input.trim()}
              className={cn(
                'flex items-center justify-center rounded-lg transition-colors flex-shrink-0 h-[36px] w-[36px]',
                isStreaming ? 'bg-red-50 text-red-500 hover:bg-red-100'
                  : input.trim() ? 'bg-text text-white hover:opacity-90'
                  : 'bg-warm-200 text-warm-400 cursor-not-allowed',
              )}
            >
              {isStreaming ? <X className="w-4 h-4" /> : <ArrowUp className="w-4 h-4" />}
            </button>
          </div>
        </div>
        </>
      )}
    </>
  )
}
